/**
 * 录音状态 UI 组件
 * 包含话筒图标、倒计时显示、录音操作按钮（取消/确认）
 */
import React from 'react';
import { Bubble, Icon } from 'tea-component';
import { useIntl } from 'umi';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import VoiceVisualizer from '../VoiceVisualizer';
import Microphone from '../../imgs/voice_input.svg';

/** 状态类配置：录音的运行时状态 */
export interface IVoiceRecorderState {
  /** 是否正在录音 */
  isRecording: boolean;
  /** 语音识别是否正在加载 */
  speekLoading: boolean;
  /** 倒计时显示文本，如 "05" */
  voiceCountdownText: string;
}

/** 样式类配置：视觉相关的可调参数 */
export interface IVoiceRecorderStyle {
  /** 语音可视化激活条数 */
  voiceActiveBar?: number;
  /** 自定义语音容器样式 */
  custVoiceStyle?: React.CSSProperties;
}

interface IVoiceRecorderProps {
  /** 状态类配置 */
  state: IVoiceRecorderState;
  /** 样式类配置 */
  style?: IVoiceRecorderStyle;
  /** 点击话筒图标 */
  onVoiceInput: () => void;
  /** 确认录音 */
  onConfirmRecording: () => void;
  /** 取消录音 */
  onCancelRecording: () => void;
}

const VoiceRecorder: React.FC<IVoiceRecorderProps> = ({
  state,
  style,
  onVoiceInput,
  onConfirmRecording,
  onCancelRecording,
}) => {
  const { formatMessage } = useIntl();
  const { isRecording, speekLoading, voiceCountdownText } = state;
  const { voiceActiveBar = 175, custVoiceStyle } = style || {};

  return (
    <div className={classNames(isRecording && 'dba-voice-container')}>
      {!isRecording ? (
        // 非录音状态：显示话筒图标
        <Bubble content={formatMessage({ id: 'dba_input_voice' })} overlayClassName='dba-nav-menu-overlay'>
          <div className="dba-voice">
            <img
              src={Microphone}
              alt=""
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onVoiceInput}
              className="dba-microphone"
            />
          </div>
        </Bubble>
      ) : (
        // 录音状态：显示倒计时和操作按钮
        <>
          <div className="dba-voice-icon-container" style={custVoiceStyle}>
            <VoiceVisualizer
              isActive={isRecording}
              isLoading={speekLoading}
              activeBars={voiceActiveBar}
            />
          </div>
          <div className="dba-countdown">
            00:
            {voiceCountdownText}
            / 00:30
          </div>
          <div className="dba-recording-actions">
            <button
              className={classNames('dba-action-btn', 'dba-cancel-btn', {
                ['dba-btn-voice-cancel-btn-disabled']: speekLoading,
              })}
              disabled={speekLoading}
              onClick={onCancelRecording}
            >
              <SvgIcon type="clonse" />
            </button>
            {speekLoading ? (
              <button
                className="dba-action-btn dba-cancel-btn dba-confirm-btn-loading"
                onClick={onConfirmRecording}
                title="loading"
              >
                <Icon
                  size="l"
                  type="loading"
                  className="dba-voice-loading-icon"
                />
              </button>
            ) : (
              <button
                className="dba-action-btn dba-confirm-btn"
                onClick={onConfirmRecording}
              >
                <SvgIcon type="right-good" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VoiceRecorder;
